import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useState } from 'react';
import {
  useAllVendorsQuery,
  useFestivalVendorsQuery,
  useRemoveFestivalVendorMutation,
  useSaveFestivalVendorMutation,
  useVendorCategoriesQuery,
} from '@/queries/vendors';
import Portal from '../../modal/Portal';
import { FestivalVendorForm } from '../../vendors';
import {
  EventRelatedItemRow,
  EventRelatedSection,
} from '../EventRelatedSection';

function vendorIdOf(entry) {
  return entry?.vendor?.vendorId ?? entry?.vendorId;
}

export function VendorsSection({ festivalId }) {
  const festivalVendorsQ = useFestivalVendorsQuery(festivalId);
  const allVendorsQ = useAllVendorsQuery();
  const categoriesQ = useVendorCategoriesQuery();
  const saveMutation = useSaveFestivalVendorMutation(festivalId);
  const removeMutation = useRemoveFestivalVendorMutation(festivalId);

  const festivalVendors = festivalVendorsQ.data ?? [];
  const vendors = allVendorsQ.data ?? [];
  const vendorCategories = categoriesQ.data ?? {};
  const loading =
    festivalId == null ||
    festivalVendorsQ.isPending ||
    festivalVendorsQ.isFetching ||
    allVendorsQ.isPending ||
    allVendorsQ.isFetching;

  const [selectedVendor, setSelectedVendor] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const openModal = (vendor) => {
    const next =
      vendor?.vendorId != null
        ? festivalVendors.find((v) => v.vendor.vendorId === vendor.vendorId) ||
          {}
        : {};
    setSelectedVendor(next);
    setShowHelp(false);
    setShowModal(true);
  };

  const onLocationChange = (location) => {
    setSelectedVendor((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        ...location,
        radius: 20,
      },
    }));
  };

  const onLocationNameChange = (locationName) => {
    setSelectedVendor((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        locationName,
      },
    }));
  };

  const handleSave = async (vendor) => {
    const updatedVendor = { ...selectedVendor, ...vendor };
    try {
      await saveMutation.mutateAsync(updatedVendor);
      setShowModal(false);
      setShowHelp(false);
      setSelectedVendor({});
    } catch {
      /* mutation toast */
    }
  };

  const requestDelete = useCallback((entry) => {
    if (!vendorIdOf(entry)) {
      return;
    }
    setPendingDelete(entry);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    const vendorId = vendorIdOf(pendingDelete);
    if (!vendorId) {
      return;
    }
    try {
      await removeMutation.mutateAsync(vendorId);
      setPendingDelete(null);
      if (vendorIdOf(selectedVendor) === vendorId) {
        setShowModal(false);
        setShowHelp(false);
        setSelectedVendor({});
      }
    } catch {
      /* mutation toast */
    }
  }, [pendingDelete, removeMutation, selectedVendor]);

  return (
    <>
      <EventRelatedSection
        icon={['fas', 'shop']}
        title="Vendors"
        loading={loading}
        onAdd={() => openModal({})}
        addLabel="Add Vendor"
      >
        {festivalVendors.map((entry) => {
          const vendor = entry.vendor;
          const categoryMeta = [vendor?.category, vendor?.subcategory]
            .filter(Boolean)
            .join(' · ');
          return (
            <EventRelatedItemRow
              key={vendorIdOf(entry)}
              label={vendor?.vendorName || 'Untitled vendor'}
              meta={categoryMeta || undefined}
              onEdit={() => openModal(vendor)}
              onDelete={() => requestDelete(entry)}
            />
          );
        })}
      </EventRelatedSection>

      <Portal
        header={
          showHelp
            ? 'Adding a Vendor'
            : selectedVendor.vendor
              ? selectedVendor.vendor.vendorName || 'Add Vendor'
              : 'Add Vendor'
        }
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setShowHelp(false);
        }}
      >
        <FestivalVendorForm
          vendor={selectedVendor}
          vendors={vendors}
          vendorCategories={vendorCategories}
          handleSave={handleSave}
          onLocationChange={onLocationChange}
          onLocationNameChange={onLocationNameChange}
          showHelp={showHelp}
          onShowHelp={setShowHelp}
        />
      </Portal>

      <Portal
        header="Remove vendor"
        open={Boolean(pendingDelete)}
        onClose={cancelDelete}
        size="sm"
      >
        <div className="py-2">
          <p className="m-0 text-base leading-relaxed text-[#121212]">
            Are you sure you want to remove this vendor from the festival?
          </p>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center rounded-[var(--bethere-radius-md)] border-2 border-primary bg-transparent px-5 font-bold text-primary transition-all hover:-translate-y-px hover:bg-primary hover:text-primary-foreground"
              onClick={cancelDelete}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] border border-transparent bg-[#c9302c] px-5 font-bold text-white transition-all hover:-translate-y-px hover:brightness-110 disabled:opacity-60"
              onClick={confirmDelete}
              disabled={removeMutation.isPending}
            >
              <FontAwesomeIcon icon={['fas', 'trash']} />
              Delete
            </button>
          </div>
        </div>
      </Portal>
    </>
  );
}
